// API helpers for /api/quotes/* and /api/quotes/:id/price.

import type {
  CreateQuoteInput,
  PricingResponseDto,
  QuoteDto,
  QuoteEntryContextDto,
  QuoteListItemDto,
  QuoteStatusDto,
  UpdateQuoteInput,
} from 'shared';

import { api } from './client.js';

export async function listQuotes(): Promise<QuoteListItemDto[]> {
  const res = await api.get<{ quotes: QuoteListItemDto[] }>('/quotes');
  return res.data.quotes;
}

export async function getQuote(id: string): Promise<QuoteDto> {
  const res = await api.get<{ quote: QuoteDto }>(`/quotes/${id}`);
  return res.data.quote;
}

// POST /api/quotes is CONDITIONALLY IDEMPOTENT: under a session bound to a Nexus
// Opportunity that already has an open (draft | priced) quote, it does not create
// — it returns that quote with `resumed: true` (HTTP 200; a fresh create is 201).
// Read the body flag, never the status code. `resumed` is what tells a caller its
// post-create side effects must NOT run: they would mutate an order the customer
// has already been editing.
export type CreateQuoteResult = {
  quote: QuoteDto;
  resumed: boolean;
};

// The browser's zone, so a new order's default title is stamped in the creating
// person's local time rather than the server's.
function browserTimeZone(): string | undefined {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
}

export async function createQuote(input: CreateQuoteInput): Promise<CreateQuoteResult> {
  const res = await api.post<CreateQuoteResult>('/quotes', {
    ...input,
    time_zone: input.time_zone ?? browserTimeZone(),
  });
  return res.data;
}

export async function updateQuote(id: string, input: UpdateQuoteInput): Promise<QuoteDto> {
  const res = await api.patch<{ quote: QuoteDto }>(`/quotes/${id}`, input);
  return res.data.quote;
}

export async function cloneQuote(id: string): Promise<QuoteDto> {
  const res = await api.post<{ quote: QuoteDto }>(`/quotes/${id}/clone`, {
    time_zone: browserTimeZone(),
  });
  return res.data.quote;
}

export async function deleteQuote(id: string): Promise<void> {
  await api.delete(`/quotes/${id}`);
}

// GET /api/quotes/entry-context — whether this session was opened from a Nexus
// Opportunity, and whether that Opportunity already has an open order. Safe for
// any authenticated session; an ordinary self-serve one answers all-nulls.
export async function getEntryContext(): Promise<QuoteEntryContextDto> {
  const res = await api.get<{ entry_context: QuoteEntryContextDto }>('/quotes/entry-context');
  return res.data.entry_context;
}

// The entry screen (QuoteNew) reads this and the orders list (QuoteList)
// invalidates it, so the query options live here instead of being restated at
// each call site. The header CTA does NOT read it — it always starts a new order
// (see StartNewOrderButton). staleTime and gcTime override the app-wide 30s
// default because the answer changes the moment an order is minted or discarded,
// and a stale "no open order" hides the in-progress order from the one screen
// that offers to resume it.
export const entryContextQuery = {
  queryKey: ['quote-entry-context'] as const,
  queryFn: getEntryContext,
  staleTime: 0,
  gcTime: 0,
};

export async function priceQuote(
  id: string,
  client_request_id?: string
): Promise<PricingResponseDto> {
  const res = await api.post<PricingResponseDto>(`/quotes/${id}/price`, {
    ...(client_request_id ? { client_request_id } : {}),
  });
  return res.data;
}

// Nexus's authoritative (control + approval_state) verdict for a quote.
// Drives the lock + pay surface (see shared QuoteStatusDto). A 503 means
// status is temporarily unavailable — we surface { ok: false } and the
// caller falls back to today's behavior rather than treating it as fatal.
// validateStatus opts in to reading the body on the non-200.
export type QuoteStatusResult =
  | { ok: true; status: QuoteStatusDto }
  | { ok: false; failure: { code: string; message: string } };

// Short-lived URL to the doc_service-rendered accepted-order PDF (read-only;
// the portal never renders/stores it). null means it isn't ready yet.
export async function getOrderDocumentUrl(id: string): Promise<string | null> {
  const res = await api.get<{ url: string | null }>(`/quotes/${id}/order-document`);
  return res.data.url;
}

export async function getQuoteStatus(id: string): Promise<QuoteStatusResult> {
  const res = await api.get<unknown>(`/quotes/${id}/status`, {
    validateStatus: () => true,
  });
  const data = res.data;
  if (data && typeof data === 'object' && 'ok' in data) {
    const d = data as { ok: unknown; status?: QuoteStatusDto; failure?: { code: string; message: string } };
    if (d.ok === true && d.status) return { ok: true, status: d.status };
    if (d.ok === false && d.failure) return { ok: false, failure: d.failure };
  }
  return { ok: false, failure: { code: 'STATUS_UNAVAILABLE', message: 'Could not check quote status.' } };
}
